<template>
  <div class="programs-page">
    <UserAppChrome />

    <main class="programs-shell">
      <UserSectionHeader
        eyebrow="Il tuo percorso"
        title="I tuoi percorsi"
        description="Compila i questionari, monitora gli step e segui l'avanzamento dei programmi preparati per te."
      />

    <section v-if="!loading && hasManualDocs" class="manual-docs">
      <div class="manual-card">
        <div class="manual-card__head">
          <div>
            <h2>Questionari manuali</h2>
            <p class="muted">Compila questi documenti speciali in aggiunta ai programmi.</p>
          </div>
        </div>
        <ul class="manual-card__list">
          <li v-for="doc in manualDocsOrdered" :key="doc.id" class="manual-item">
            <div class="manual-item__meta">
              <strong>{{ doc.stepName || manualDocTypeLabel(doc) }}</strong>
              <small
                v-if="doc.stepName && doc.stepName !== manualDocTypeLabel(doc)"
                class="muted"
              >
                {{ manualDocTypeLabel(doc) }}
              </small>
            </div>
            <div class="manual-item__actions">
              <span class="manual-item__date">{{ manualDocDateLabel(doc) }}</span>
              <button type="button" class="manual-item__btn" @click="openManualDocument(doc)">
                Apri
              </button>
            </div>
          </li>
        </ul>
      </div>
    </section>

    <div v-if="loading" class="loader-min">Caricamento…</div>
    <div v-else-if="groupedPercorsi.length === 0" class="programs-empty">
      Non ci sono programmi disponibili.
    </div>

    <section v-else class="group-list">
      <div
        v-for="group in groupedPercorsi"
        :key="group.id"
        class="group-block"
        :class="{
          'group-block--locked': groupHasLockedPrevious(group),
          'group-block--active': isActiveGroup(group.id),
          'group-block--open': openGroups[group.id]
        }"
      >
        <button
          type="button"
          class="group-summary"
          @click="toggleGroupOpen(group.id)"
          :aria-expanded="openGroups[group.id] ? 'true' : 'false'"
        >
          <div class="group-title">
            <h2>{{ group.productLabel }}</h2>
            <span v-if="group.totalPrograms > 1" class="group-chip">
              {{ group.totalPrograms }} programmi
            </span>
            <span
              v-if="groupHasLockedPrevious(group)"
              class="group-chip group-chip--warn"
            >
              Bloccato (precedente)
            </span>
          </div>
          <div v-if="group.startAt || group.expireAt" class="group-dates">
            <span v-if="group.startAt">
              Inizio:
              <strong>{{ formatDateOnly(group.startAt) }}</strong>
            </span>
            <span v-if="group.expireAt">
              Scadenza:
              <strong>{{ formatDateOnly(group.expireAt) }}</strong>
            </span>
          </div>
          <span class="chev" :class="{ open: openGroups[group.id] }" aria-hidden="true">▾</span>
        </button>

        <div v-if="openGroups[group.id]" class="group-body">
          <article
            v-for="p in group.percorsi"
            :key="p.id"
            class="card"
            :class="{
              'card--locked': isPercorsoLocked(p),
              'card--active': isActiveProgram(group.id, p.id)
            }"
          >
          <button class="card-head" @click="toggleOpen(group.id, p.id)">
            <div class="head-left">
              <div class="title">{{ renderProgramTitle(p.title) }}</div>
              <div class="sub muted">
                <span v-if="p.productName">{{ p.productName }}</span>
                  <span v-if="p.sequenceTotal">
                    · {{ p.sequenceIndex }}/{{ p.sequenceTotal }}
                  </span>
                </div>
              </div>
              <div class="head-right">
                <span v-if="isPercorsoLocked(p)" class="pill pill-locked">
                  {{ lockChipLabel(p) }}
                </span>
                <span v-else-if="isActiveProgram(group.id, p.id)" class="pill pill-active">
                  Programma attivo
                </span>
                <span class="pill" :class="progressClass(p)">{{ progressLabel(p) }}</span>
                <span class="chev" :class="{ open: openIds[p.id] }" aria-hidden="true">▾</span>
              </div>
            </button>

            <div v-show="openIds[p.id]" class="card-body">
              <div v-if="isPercorsoLocked(p)" class="lock-banner">
                <span class="material-icons lock-banner__icon" aria-hidden="true">lock</span>
                <div class="lock-banner__text">{{ lockMessage(p) }}</div>
              </div>

              <ul class="steps">
                <li
                  v-for="row in stepsToRender(p)"
                  :key="row.key + '_' + row.realIndex"
                  class="step-row"
                  :class="{
                    'step-row--locked': isPercorsoLocked(p),
                    'step-row--complete': isStepDone(row)
                  }"
                >
                  <div class="step-content">
                    <div class="step-header">
                      <div class="step-title">
                        <span>{{ row.step?.name || 'Step' }}</span>
                        <span v-if="row.isNext" class="badge badge-next">Prossimo step</span>
                        <span
                          v-if="row.key === 'consulenza' && row.step?.consulenzaType"
                          class="badge badge-type"
                        >
                          {{ row.step.consulenzaType }}
                        </span>
                      </div>

                      <div class="step-actions">
                        <div class="status-badges">
                          <span class="badge" :class="stepStatusClass(row)">
                            {{ stepStatusLabel(row) }}
                          </span>
                          <span
                            v-if="row.step?.adminStatus === 'needs_update'"
                            class="badge badge-amber badge-update"
                            title="Il team ha richiesto un aggiornamento di questo step"
                          >
                            Aggiornamento richiesto
                          </span>
                        </div>
                        <button
                          v-if="canOpenRow(row)"
                          type="button"
                          class="step-open-btn"
                          :disabled="isRowActionDisabled(p, row)"
                          :aria-label="row.key === 'check_finale' ? 'Apri check finale' : 'Apri questionario'"
                          @click="openRow(p, row)"
                        >
                          <span class="material-icons" aria-hidden="true">open_in_new</span>
                          <span class="step-open-label">
                            {{ row.key === 'check_finale' ? 'Apri check finale' : 'Apri questionario' }}
                          </span>
                        </button>
                      </div>
                    </div>

                    <div class="meta">
                      <div v-if="row.step?.plannedAt">
                        Previsto {{ formatDateOnly(row.step.plannedAt) }}
                      </div>
                      <div v-if="row.step?.doneAt" class="done-label">
                        Completato {{ formatDateOnly(row.step.doneAt) }}
                      </div>
                      <div v-if="row.step?.adminStatus === 'needs_update'" class="update-note">
                        <strong>Nota team:</strong> {{ row.step?.adminNote || 'Aggiornamento richiesto' }}
                      </div>
                      <div v-if="row.key === 'consulenza' && row.step?.TrainerNome">
                        Professionista: {{ row.step.TrainerNome }}
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </article>
        </div>
      </div>
    </section>

      <p v-if="error" class="err">{{ error }}</p>
    </main>
  </div>
</template>

<script>
import { ref, watch, computed, onBeforeUnmount } from 'vue';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';
import { useRoute, useRouter } from 'vue-router';
import UserAppChrome from './UserAppChrome.vue';
import UserSectionHeader from './UserSectionHeader.vue';
import { computeProgrammeLocks, isProgrammeComplete } from '@/lib/programmeLocks';

const STEP_ORDER = {
  questionario: 3,
  consegna: 6,
  consulenza: 10,
  check_finale: 12
};

const MANUAL_FORM_TYPES = {
  questionario: {
    stepKey: 'questionario',
    label: 'Questionario iniziale',
    routeName: 'ProgrammiUserQuestionario',
    type: 'user_programmi_questionario',
  },
  check_finale: {
    stepKey: 'check_finale',
    label: 'Check finale',
    routeName: 'ProgrammiUserCheckFinale',
    type: 'user_programmi_check_finale',
  },
};

export default {
  name: 'ProgrammiUser',
  components: { UserAppChrome, UserSectionHeader },
  setup() {
    const auth = getAuth();
    const db = getFirestore();
    const route = useRoute();
    const router = useRouter();

    const uid = ref('');
    const percorsi = ref([]);
    const manualDocs = ref([]);
    const percorsiVisibili = computed(() => (percorsi.value || []).filter(p => !p.consulenzeOnly));
    const loading = ref(true);
    const error = ref('');
    const openIds = ref({});
    const openGroups = ref({});
    let unsubscribeManualDocs = null;
    const manualDocsOrdered = computed(() => {
      const list = Array.isArray(manualDocs.value) ? [...manualDocs.value] : [];
      list.sort((a, b) => {
        const aTime = toDateSafe(a.updatedAt || a.createdAt)?.getTime() || 0;
        const bTime = toDateSafe(b.updatedAt || b.createdAt)?.getTime() || 0;
        return bTime - aTime;
      });
      return list;
    });
    const hasManualDocs = computed(() => manualDocsOrdered.value.length > 0);

    const recognizedDeepLinkSteps = new Set(['questionario', 'check_finale']);
    const parseDeepLink = (query = {}) => {
      if (!query) return null;
      const rawStep = typeof query.step === 'string' ? query.step.trim() : '';
      if (!recognizedDeepLinkSteps.has(rawStep)) return null;
      const percorsoId =
        typeof query.percorso === 'string' && query.percorso ? query.percorso : null;
      return { stepKey: rawStep, percorsoId };
    };

    const deepLinkTarget = ref(parseDeepLink(route.query));
    const deepLinkHandled = ref(false);

    const clearDeepLinkQuery = () => {
      const nextQuery = { ...route.query };
      let changed = false;
      if ('step' in nextQuery) {
        delete nextQuery.step;
        changed = true;
      }
      if ('percorso' in nextQuery) {
        delete nextQuery.percorso;
        changed = true;
      }
      if (!changed) return;
      router.replace({ path: route.path, query: nextQuery });
    };

    const toComparableString = (value) => {
      if (!value) return '';
      if (typeof value === 'string') return value;
      if (value instanceof Date) return value.toISOString();
      if (typeof value?.toDate === 'function') {
        try { return value.toDate().toISOString(); }
        catch { return ''; }
      }
      return String(value);
    };

    const toDateSafe = (value) => {
      if (value === undefined || value === null) return null;
      if (typeof value?.toDate === 'function') {
        try {
          const converted = value.toDate();
          return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
        } catch {
          return null;
        }
      }
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }
      if (typeof value === 'string' || typeof value === 'number') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    };

    const toMillis = (value) => {
      if (value == null) return NaN;
      if (typeof value?.toMillis === 'function') {
        try { return value.toMillis(); } catch { return NaN; }
      }
      const date = toDateSafe(value);
      return date ? date.getTime() : NaN;
    };

    const stepKeyFromName = (name = '') => {
      const n = name.toLowerCase();
      if (n.includes('check') || n.includes('scadenza')) return 'check_finale';
      if (n.includes('questionario')) return 'questionario';
      if (n.includes('consegna')) return 'consegna';
      if (n.includes('consulenza')) return 'consulenza';
      return null;
    };

    const resolveManualConfigByDoc = (docRow = {}) => {
      const key = docRow.stepKey && MANUAL_FORM_TYPES[docRow.stepKey]
        ? docRow.stepKey
        : docRow.type === MANUAL_FORM_TYPES.check_finale?.type
        ? 'check_finale'
        : 'questionario';
      return MANUAL_FORM_TYPES[key] || MANUAL_FORM_TYPES.questionario;
    };

    const manualDocTypeLabel = (docRow) => {
      const config = resolveManualConfigByDoc(docRow);
      return config.label;
    };

    const openManualDocument = (docRow) => {
      if (!docRow?.id) return;
      const config = resolveManualConfigByDoc(docRow);
      router.push({
        name: config.routeName,
        query: {
          questionarioDocId: docRow.id,
          stepKey: config.stepKey,
          manualStandalone: '1',
        },
      });
    };

    const stepsToRender = (percorso) => {
      const all = Array.isArray(percorso?.steps) ? percorso.steps : [];
      if (!all.length) return [];

      const normalized = all
        .map((step, idx) => {
          const key = stepKeyFromName(step?.name || '');
          if (!key) return null;
          const type = (step?.consulenzaType || '').toLowerCase();
          const plannedAt = step?.plannedAt || null;
          const plannedMillis = toMillis(plannedAt);
          const isDone = isStepEffectivelyDone(step);
          let order = STEP_ORDER[key] ?? 99;
          if (key === 'consulenza' && type.includes('nutriz')) {
            order = 2;
          }
          return {
            key,
            realIndex: idx,
            step,
            isDone,
            order,
            plannedAt,
            plannedMillis
          };
        })
        .filter(Boolean);

      normalized.sort((a, b) => {
        const aHasTime = Number.isFinite(a.plannedMillis);
        const bHasTime = Number.isFinite(b.plannedMillis);
        if (aHasTime || bHasTime) {
          if (aHasTime && bHasTime && a.plannedMillis !== b.plannedMillis) {
            return a.plannedMillis - b.plannedMillis;
          }
          if (aHasTime && !bHasTime) return -1;
          if (!aHasTime && bHasTime) return 1;
        }
        if (a.order !== b.order) return a.order - b.order;
        return a.realIndex - b.realIndex;
      });

      const firstPendingIndex = normalized.findIndex(item => !item.isDone);
      if (firstPendingIndex >= 0) {
        normalized[firstPendingIndex] = {
          ...normalized[firstPendingIndex],
          isNext: true
        };
      }

      const consegnaRow = normalized.find((item) => item.key === 'consegna');
      const consegnaDone = consegnaRow ? consegnaRow.isDone : true;
      normalized.forEach((item) => {
        if (item.key === 'check_finale') {
          const checkAlreadyDone = item.isDone;
          const hasOverride = !!item.step?.allowCheckOverride;
          item.isLockedByConsegna = !checkAlreadyDone && !consegnaDone && !hasOverride;
        }
      });

      return normalized;
    };

    const renderProgramTitle = (rawTitle) => {
      if (!rawTitle) return 'Programma';
      return rawTitle
        .replace(/Percorsi/g, 'Programmi')
        .replace(/percorsi/g, 'programmi')
        .replace(/Percorso/g, 'Programma')
        .replace(/percorso/g, 'programma');
    };

    const groupedPercorsi = computed(() => {
      const list = percorsiVisibili.value || [];
      const groups = new Map();
      const groupMeta = new Map();

      for (const percorso of list) {
        const gid = percorso.sequenceGroupId || percorso.id;
        if (!groups.has(gid)) {
          groups.set(gid, []);
        }
        groups.get(gid).push(percorso);

        const anchor = toComparableString(percorso.plannedStartAt || percorso.createdAt);
        const meta = groupMeta.get(gid) || { anchor: anchor || '' };
        if (!meta.anchor || (anchor && anchor < meta.anchor)) {
          meta.anchor = anchor;
        }
        groupMeta.set(gid, meta);
      }

      const groupsData = Array.from(groups.keys()).map((gid) => {
        const bucket = groups.get(gid) || [];
        bucket.sort((x, y) => {
          const ix = Number(x.sequenceIndex) || 0;
          const iy = Number(y.sequenceIndex) || 0;
          if (ix !== iy) return ix - iy;
          const dx = toComparableString(x.plannedStartAt || x.createdAt);
          const dy = toComparableString(y.plannedStartAt || y.createdAt);
          return dx.localeCompare(dy);
        });

        const activeInBucket = bucket.find(
          (p) => !isPercorsoLocked(p) && !isPercorsoFullyCompleted(p)
        );

        const first = bucket[0] || {};
        const totalPrograms = Number(first.sequenceTotal) || bucket.length;
        const productLabel = first.productName || renderProgramTitle(first.title);

        const startAt = bucket.reduce((min, p) => {
          const val = toDateSafe(p.plannedStartAt || p.createdAt);
          if (!val) return min;
          if (!min || val < min) return val;
          return min;
        }, null);

        const expireAt = bucket.reduce((max, p) => {
          const val = toDateSafe(p.expireAt);
          if (!val) return max;
          if (!max || val > max) return val;
          return max;
        }, null);

        return {
          id: gid,
          percorsi: bucket,
          totalPrograms,
          productLabel,
          startAt,
          expireAt,
          hasActive: !!activeInBucket,
          anchor: groupMeta.get(gid)?.anchor || ''
        };
      });

      groupsData.sort((a, b) => {
        if (a.hasActive !== b.hasActive) return a.hasActive ? -1 : 1;
        return (b.anchor || '').localeCompare(a.anchor || '');
      });

      return groupsData.map((entry) => {
        const rest = { ...entry };
        delete rest.anchor;
        return rest;
      });
    });

    const dateFormatter = new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const formatDateOnly = (value) => {
      const date = toDateSafe(value);
      return date ? dateFormatter.format(date) : '';
    };

    const manualDocDateLabel = (docRow) => {
      const stamp = docRow.updatedAt || docRow.createdAt;
      return stamp ? formatDateOnly(stamp) : '—';
    };

    const normalizeConsulenzaStatus = (value) => {
      if (value === undefined || value === null) return '';
      return String(value).trim().toLowerCase();
    };
    const isConsulenzaStatusDone = (status) => {
      const normalized = normalizeConsulenzaStatus(status);
      return normalized === 'fatta'
        || normalized === 'fatto'
        || normalized === 'rifiutata'
        || normalized === 'rifiutato'
        || normalized === 'scalata'
        || normalized === 'scalato';
    };
    const isBlockingStepForSequence = (step) => {
      const name = (step?.name || '').toLowerCase();
      if (!name) return true;
      if (name.includes('feedback')) return false;
      return true;
    };
    const isStepEffectivelyDone = (step) => {
      if (!step) return false;
      const name = (step?.name || '').toLowerCase();
      if (name.includes('consulenza') && isConsulenzaStatusDone(step?.consulenzaStatus)) {
        return true;
      }
      return !!step?.done;
    };
    const blockingStepsOnly = (steps = []) =>
      (Array.isArray(steps) ? steps : []).filter(isBlockingStepForSequence);
    const countBlockingSteps = (steps = []) => blockingStepsOnly(steps).length;
    const countBlockingDoneSteps = (steps = []) =>
      blockingStepsOnly(steps).filter(isStepEffectivelyDone).length;
    const countEffectiveDoneSteps = (steps = []) =>
      (Array.isArray(steps) ? steps : []).filter(isStepEffectivelyDone).length;

    const progressLabel = (p) => {
      const steps = Array.isArray(p.steps) ? p.steps : [];
      const blockingTotal = countBlockingSteps(steps);
      const tot = blockingTotal || steps.length || 0;
      if (!tot) return '0/0 step';
      const done = blockingTotal ? countBlockingDoneSteps(steps) : countEffectiveDoneSteps(steps);
      return `${done}/${tot} step`;
    };

    const progressClass = (p) => {
      const steps = Array.isArray(p.steps) ? p.steps : [];
      const blockingTotal = countBlockingSteps(steps);
      const tot = blockingTotal || steps.length;
      const done = blockingTotal ? countBlockingDoneSteps(steps) : countEffectiveDoneSteps(steps);
      if (!tot || done === 0) return 'pill-gray';
      if (done < tot) return 'pill-amber';
      return 'pill-green';
    };

    const isPercorsoFullyCompleted = (percorso) => isProgrammeComplete(percorso);

    const programmeLocks = computed(() => computeProgrammeLocks(percorsiVisibili.value));
    const sequentialLocks = computed(() => programmeLocks.value.sequentialLocks);

    const isSequentiallyLocked = (percorso) =>
      !!(percorso && sequentialLocks.value[percorso.id]);

    const previousGroupLocks = computed(() => programmeLocks.value.previousLocks);

    const isPercorsoBlockedByPreviousGroup = (percorso) =>
      !!(percorso && previousGroupLocks.value[percorso.id]);

    const percorsoLockReason = (percorso) => {
      if (percorso?.historyLocked) return 'history';
      if (isPercorsoBlockedByPreviousGroup(percorso)) return 'previous_group';
      if (isSequentiallyLocked(percorso)) return 'sequence';
      return null;
    };

    const isPercorsoLocked = (percorso) => percorsoLockReason(percorso) !== null;

    const groupHasLockedPrevious = (group) => {
      if (!group) return false;
      return (group.percorsi || []).some(p => isPercorsoBlockedByPreviousGroup(p));
    };

    const activeProgramByGroup = computed(() => {
      const map = {};
      for (const group of groupedPercorsi.value || []) {
        const active = (group.percorsi || []).find(
          (p) => !isPercorsoLocked(p) && !isPercorsoFullyCompleted(p)
        );
        if (active?.id) map[group.id] = active.id;
      }
      return map;
    });

    const isActiveGroup = (groupId) => !!activeProgramByGroup.value[groupId];
    const isActiveProgram = (groupId, percorsoId) => activeProgramByGroup.value[groupId] === percorsoId;

    const lockChipLabel = (percorso) => {
      const reason = percorsoLockReason(percorso);
      if (reason === 'history') return 'Storico';
      if (reason === 'previous_group') return 'Bloccato (precedente)';
      if (reason === 'sequence') return 'Bloccato';
      return '';
    };

    const lockMessage = (percorso) => {
      const reason = percorsoLockReason(percorso);
      if (reason === 'sequence') {
        const seqIndex = Number(percorso?.sequenceIndex) || null;
        const seqTotal = Number(percorso?.sequenceTotal) || null;
        if (seqIndex && seqTotal && seqIndex > 1) {
          return `Completa il programma ${seqIndex - 1}/${seqTotal} per sbloccare questo programma.`;
        }
        return 'Completa il programma precedente per sbloccarlo.';
      }
      if (reason === 'previous_group') {
        return 'Questo programma è stato acquistato dopo un percorso non ancora concluso. Completa il precedente o attendi lo sblocco.';
      }
      if (reason === 'history') {
        return 'Questo programma è stato archiviato dal team.';
      }
      return '';
    };

    const isRowActionDisabled = (percorso, row) => {
      if (isPercorsoLocked(percorso)) return true;
      if (row?.key === 'check_finale' && row.isLockedByConsegna) return true;
      return false;
    };

    const stepStatusLabel = (row) => {
      if (row.key === 'consulenza') {
        return row.step?.consulenzaStatus || 'Da fare';
      }
      return row.step?.done ? 'Fatto' : 'Da fare';
    };

    const isConsulenzaDone = (row) => isConsulenzaStatusDone(row?.step?.consulenzaStatus);

    const stepStatusClass = (row) => {
      if (row.key === 'consulenza') {
        const status = (row.step?.consulenzaStatus || '').toLowerCase();
        if (status === 'prenotata' || status === 'prenotato') return 'badge-prenotata';
        if (status === 'rifiutata' || status === 'rifiutato') return 'badge-rifiutata';
        if (isConsulenzaDone(row)) return 'badge-done';
        return 'badge-todo';
      }
      return row.step?.done ? 'badge-done' : 'badge-todo';
    };

    const isStepDone = (row) => {
      if (!row) return false;
      if (row.isDone !== undefined) return row.isDone;
      return row.key === 'consulenza' ? isConsulenzaDone(row) : !!row.step?.done;
    };

    const canOpenRow = (row) => row?.key === 'questionario' || row?.key === 'check_finale';

    const openRow = (percorso, row) => {
      if (!row || !canOpenRow(row) || isRowActionDisabled(percorso, row)) return;
      if (!uid.value) return;
      const targetRoute = row.key === 'check_finale' ? 'ProgrammiUserCheckFinale' : 'ProgrammiUserQuestionario';
      router.push({
        name: targetRoute,
        query: {
          percorsoId: percorso.id,
          stepIndex: row.realIndex,
          stepKey: row.key,
          userId: uid.value
        }
      });
    };

    const tryHandleDeepLink = () => {
      if (deepLinkHandled.value) return;
      const target = deepLinkTarget.value;
      if (!target || !percorsi.value.length) return;

      const { stepKey, percorsoId } = target;
      const candidates = percorsoId
        ? percorsiVisibili.value.filter(p => p.id === percorsoId)
        : percorsiVisibili.value;

      for (const percorso of candidates) {
        const rows = stepsToRender(percorso);
        const row = rows.find(r => r.key === stepKey);
        if (!row || !canOpenRow(row)) continue;

        openIds.value = { ...openIds.value, [percorso.id]: true };
        openRow(percorso, row);
        deepLinkHandled.value = true;
        deepLinkTarget.value = null;
        clearDeepLinkQuery();
        break;
      }
    };

    watch(percorsiVisibili, () => tryHandleDeepLink(), { immediate: true });
    watch(deepLinkTarget, () => tryHandleDeepLink());
    watch(
      () => route.query,
      (newQuery) => {
        deepLinkTarget.value = parseDeepLink(newQuery);
        deepLinkHandled.value = false;
        tryHandleDeepLink();
      },
      { immediate: true }
    );

    const toggleOpen = (groupId, id) => {
      if (!id) return;
      const isOpen = !!openIds.value[id];
      const next = {};
      if (!isOpen) next[id] = true;
      openIds.value = next;
      // ensure group stays open when a card is toggled
      if (groupId) {
        openGroups.value = { ...openGroups.value, [groupId]: true };
      }
    };

    const toggleGroupOpen = (groupId) => {
      if (!groupId) return;
      openGroups.value = { ...openGroups.value, [groupId]: !openGroups.value[groupId] };
    };

    let unsubscribePercorsi = null;
    const fetchPercorsi = () => {
      if (!uid.value) return;
      if (unsubscribePercorsi) {
        unsubscribePercorsi();
        unsubscribePercorsi = null;
      }
      loading.value = true;
      const qPerc = query(collection(db, 'percorsi'), where('uid', '==', uid.value));
      unsubscribePercorsi = onSnapshot(qPerc, (qs) => {
        const rows = qs.docs.map(d => ({ id: d.id, ...d.data() }));

        const groups = new Map();
        const groupMeta = new Map();
        for (const p of rows) {
          const gid = p.sequenceGroupId || p.id;
          const bucket = groups.get(gid) || [];
          bucket.push(p);
          groups.set(gid, bucket);

          const anchor = toComparableString(p.plannedStartAt || p.createdAt);
          const meta = groupMeta.get(gid) || { anchor: anchor || '' };
          if (!meta.anchor || (anchor && anchor < meta.anchor)) {
            meta.anchor = anchor;
          }
          groupMeta.set(gid, meta);
        }

        const order = Array.from(groups.keys()).sort((a, b) => {
          const aa = groupMeta.get(a)?.anchor || '';
          const bb = groupMeta.get(b)?.anchor || '';
          return bb.localeCompare(aa);
        });

        const sorted = [];
        for (const gid of order) {
          const bucket = groups.get(gid) || [];
          bucket.sort((x, y) => {
            const ix = Number(x.sequenceIndex) || 0;
            const iy = Number(y.sequenceIndex) || 0;
            if (ix !== iy) return ix - iy;
            const dx = toComparableString(x.plannedStartAt || x.createdAt);
            const dy = toComparableString(y.plannedStartAt || y.createdAt);
            return dx.localeCompare(dy);
          });
          sorted.push(...bucket);
        }

        percorsi.value = sorted;

        const nextOpen = {};
        for (const percorso of sorted) {
          if (openIds.value[percorso.id]) nextOpen[percorso.id] = true;
        }
        openIds.value = nextOpen;

        // default: apri il gruppo con programma attivo (se non bloccato dal precedente), preservando quelli già aperti
        const lockedGroupIds = new Set(
          (groupedPercorsi.value || [])
            .filter(g => groupHasLockedPrevious(g))
            .map(g => g.id)
        );
        const groupIds = Array.from(new Set(sorted.map(p => p.sequenceGroupId || p.id)));
        const nextGroups = {};
        groupIds.forEach((gid, idx) => {
          const activeId = activeProgramByGroup.value[gid];
          if (openGroups.value[gid] !== undefined) {
            nextGroups[gid] = openGroups.value[gid];
          } else if (lockedGroupIds.has(gid)) {
            nextGroups[gid] = false;
          } else if (activeId) {
            nextGroups[gid] = true;
          } else {
            nextGroups[gid] = idx === 0;
          }
        });
        openGroups.value = nextGroups;
        loading.value = false;
      }, (e) => {
        error.value = e.message || String(e);
        loading.value = false;
      });
    };

    const subscribeManualDocs = () => {
      if (unsubscribeManualDocs) {
        unsubscribeManualDocs();
        unsubscribeManualDocs = null;
      }
      if (!uid.value) {
        manualDocs.value = [];
        return;
      }
      const qManual = query(
        collection(db, 'questionari'),
        where('uid', '==', uid.value),
        where('manualStandalone', '==', true)
      );
      unsubscribeManualDocs = onSnapshot(qManual, (qs) => {
        manualDocs.value = qs.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      });
    };

    onAuthStateChanged(auth, (user) => {
      if (user) {
        uid.value = user.uid;
        fetchPercorsi();
        subscribeManualDocs();
      } else {
        uid.value = '';
        percorsi.value = [];
        openIds.value = {};
        manualDocs.value = [];
        loading.value = false;
        if (unsubscribePercorsi) {
          unsubscribePercorsi();
          unsubscribePercorsi = null;
        }
        if (unsubscribeManualDocs) {
          unsubscribeManualDocs();
          unsubscribeManualDocs = null;
        }
      }
    });

    onBeforeUnmount(() => {
      if (unsubscribePercorsi) {
        unsubscribePercorsi();
        unsubscribePercorsi = null;
      }
      if (unsubscribeManualDocs) {
        unsubscribeManualDocs();
        unsubscribeManualDocs = null;
      }
    });

    return {
      groupedPercorsi,
      loading,
      error,
      openIds,
      openGroups,
      activeProgramByGroup,
      isActiveGroup,
      isActiveProgram,
      stepsToRender,
      toggleOpen,
      toggleGroupOpen,
      renderProgramTitle,
      progressLabel,
      progressClass,
      isPercorsoLocked,
      lockChipLabel,
      lockMessage,
      isPercorsoBlockedByPreviousGroup,
      groupHasLockedPrevious,
      isRowActionDisabled,
      canOpenRow,
      openRow,
      stepStatusLabel,
      stepStatusClass,
      isStepDone,
      formatDateOnly,
      manualDocsOrdered,
      hasManualDocs,
      manualDocTypeLabel,
      manualDocDateLabel,
      openManualDocument,
    };
  }
};
</script>

<style scoped>
:host {
  display: block;
  min-height: 100vh;
  background: radial-gradient(circle at top, #0b1120 0%, #020617 55%, #01040c 100%);
  color-scheme: dark;
}

.template-container {
  display: grid;
  gap: 18px;
  padding: 12px 10px 28px;
  max-width: 980px;
  margin: 0 auto;
}

.muted { color:#94a3b8; }
.err { color:#f87171; margin-top:10px; }
.loader-min { color:#e2e8f0; }

.m-header h1 {
  color:#f8fafc;
  text-align:center;
  font-size: 1.5rem;
  margin: 8px 0 4px;
  letter-spacing:.2px;
}
.m-header p {
  color:#cbd5e1;
  text-align:center;
  margin: 0 0 8px;
  font-size: .92rem;
}

.manual-docs {
  background: rgba(15, 23, 42, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  padding: 14px;
  box-shadow: 0 18px 36px rgba(2, 6, 23, 0.5);
}

.manual-card__head h2 {
  margin: 0;
  font-size: 1.05rem;
  color: #f8fafc;
}

.manual-card__head p {
  margin: 4px 0 0;
  color: #a5b4fc;
}

.manual-card__list {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 10px;
}

.manual-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.6);
  flex-wrap: wrap;
  gap: 8px;
}

.manual-item__meta strong {
  display: block;
  color: #f1f5f9;
  font-size: 0.95rem;
}

.manual-item__meta small {
  color: #cbd5f5;
}

.manual-item__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.manual-item__date {
  font-size: 0.78rem;
  color: #94a3b8;
}

.manual-item__btn {
  border: none;
  border-radius: 999px;
  padding: 6px 14px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  background: linear-gradient(135deg, #38bdf8, #22d3ee);
  color: #0f172a;
  box-shadow: 0 12px 24px rgba(34, 211, 238, 0.3);
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}

.manual-item__btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 20px 36px rgba(34, 211, 238, 0.45);
}

.group-list {
  display: grid;
  gap: 18px;
}

.group-block {
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.group-block--active {
  border-color: rgba(34, 197, 94, 0.55);
}
.group-block--locked {
  border-color: rgba(239, 68, 68, 0.6);
}

.group-summary {
  display: grid;
  gap: 6px;
  align-items: start;
  padding: 8px 6px;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.group-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.group-title h2 {
  margin: 0;
  color: #f1f5f9;
  font-size: 1rem;
  font-weight: 700;
}

.group-chip {
  font-size: 0.7rem;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(30, 41, 59, 0.7);
  color: #cbd5e1;
}
.group-chip--warn {
  background: rgba(248, 113, 113, 0.12);
  color: #fecdd3;
  border-color: rgba(248, 113, 113, 0.35);
}

.group-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.8rem;
  color: #cbd5e1;
}

.group-dates strong {
  color: #38bdf8;
}

.group-body { display: grid; gap: 10px; }

.card {
  border:1px solid rgba(148, 163, 184, 0.25);
  border-radius:16px;
  background: rgba(2, 6, 12, 0.75);
  overflow:hidden;
  padding:10px;
}

.card--active {
  border-color: rgba(34, 197, 94, 0.55);
  background: linear-gradient(135deg, rgba(6, 95, 70, 0.18), rgba(10, 135, 97, 0.12));
}

.card--locked {
  border-color: rgba(148, 163, 184, 0.65);
  background: rgba(226, 232, 240, 0.12);
}

.card--locked .card-head {
  background: rgba(71, 85, 105, 0.45);
}

.card--locked .card-head:hover {
  background: rgba(71, 85, 105, 0.55);
}

.card--locked .head-left .title {
  color: #e2e8f0;
}

.card--locked .pill {
  border-color: rgba(226, 232, 240, 0.6);
}

.card--locked .card-body {
  background: rgba(15, 23, 42, 0.5);
}

.card-head{
  width:100%;
  display:flex;
  justify-content:space-between;
  align-items:center;
  background: rgba(8, 23, 45, 0.88);
  padding:10px 12px;
  border:0;
  cursor:pointer;
  gap: 8px;
  transition: background 0.2s ease, border-color 0.2s ease;
}
.card-head:hover{
  background: rgba(12, 32, 60, 0.92);
}
.head-left {
  display:flex;
  flex-direction:column;
  gap:4px;
  text-align:left;
}
.head-left .title { font-weight:700; color:#f1f5f9; font-size:1rem; }
.head-left .sub   { font-size:.8rem; color:#94a3b8; }
.head-right{ display:flex; align-items:center; gap:6px; flex-wrap: wrap; }
.chev {
  transition: transform .18s ease;
  color:#cbd5e1;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:16px;
}
.chev.open { transform: rotate(180deg); }

.pill{
  font-size:.72rem;
  padding:4px 8px;
  border-radius:999px;
  border:1px solid transparent;
  background:#1e293b;
  color:#cfe1ff;
  text-transform: uppercase;
  letter-spacing: .3px;
}
.pill-gray  { background:#1f2937; border-color:rgba(148, 163, 184, 0.3); color:#cbd5e1; }
.pill-amber { background:#3b2413; border-color:#a16207; color:#fbbf24; }
.pill-green { background:#0f2e21; border-color:#16a34a; color:#86efac; }
.pill-locked { background:rgba(226, 232, 240, 0.85); border-color:rgba(248, 250, 252, 0.6); color:#0f172a; }
.pill-active { background: rgba(34, 197, 94, 0.16); border-color: rgba(34, 197, 94, 0.6); color: #bbf7d0; }

.card-body { padding: 12px 12px 14px; background: rgba(15, 23, 42, 0.65); }
.lock-banner {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  border: 1px dashed rgba(148, 163, 184, 0.55);
  background: rgba(226, 232, 240, 0.15);
  color: #e2e8f0;
  padding: 8px 10px;
  border-radius: 12px;
  margin-bottom: 10px;
}
.lock-banner__icon {
  font-size: 18px;
  color: #cbd5e1;
}
.lock-banner__text {
  line-height: 1.4;
}
.steps { list-style:none; margin:0; padding:0; display:grid; gap:10px; }

.step-row{
  display:flex;
  flex-direction:column;
  gap:8px;
  padding:10px 12px;
  border:1px solid rgba(51, 65, 85, 0.55);
  border-radius:14px;
  background: rgba(8, 15, 31, 0.85);
  transition: border-color .25s ease, background .25s ease, transform .2s ease;
}
.step-row--locked {
  opacity: 0.55;
  filter: grayscale(0.25);
  border-color: rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.65);
}
.step-row--complete {
  border-color: rgba(34, 197, 94, 0.35);
  background: rgba(12, 22, 34, 0.85);
}
.step-row:hover {
  border-color: rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.step-content {
  display:flex;
  flex-direction:column;
  gap:8px;
  flex:1;
}

.step-header {
  display:flex;
  flex-direction:column;
  gap:8px;
}

.step-title {
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  gap:8px;
  color:#e2e8f0;
  font-weight:700;
}

.step-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.status-badges {
  display:flex;
  flex-wrap:wrap;
  gap:6px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 0.7rem;
  line-height: 1.1rem;
  border: 1px solid transparent;
  background: rgba(30, 41, 59, 0.8);
  color: #cbd5e1;
  letter-spacing: .2px;
}

.badge-next {
  background: rgba(59, 130, 246, 0.22);
  border-color: rgba(59, 130, 246, 0.5);
  color: #bfdbfe;
}

.badge-manual {
  background: rgba(99, 102, 241, 0.18);
  border-color: rgba(129, 140, 248, 0.6);
  color: #c7d2fe;
}

.badge-type {
  background: rgba(22, 163, 74, 0.18);
  border-color: rgba(22, 163, 74, 0.45);
  color: #86efac;
}

.badge-update {
  background: rgba(249, 115, 22, 0.18);
  border-color: rgba(249, 115, 22, 0.6);
  color: #f97316;
}

.update-note {
  color: #f97316;
  font-weight: 600;
}

.badge-warn {
  background: rgba(249, 115, 22, 0.18);
  border-color: rgba(249, 115, 22, 0.45);
  color: #fb923c;
}

.badge-worked {
  background: rgba(168, 85, 247, 0.18);
  border-color: rgba(139, 92, 246, 0.45);
  color: #ede9fe;
}

.badge-daily {
  background: rgba(59, 130, 246, 0.18);
  border-color: rgba(59, 130, 246, 0.45);
  color: #dbeafe;
}

.badge-todo {
  background: rgba(30, 41, 59, 0.7);
  border-color: rgba(71, 85, 105, 0.6);
  color: #cbd5e1;
}

.badge-done {
  background: rgba(22, 163, 74, 0.2);
  border-color: rgba(22, 163, 74, 0.5);
  color: #bbf7d0;
}

.badge-prenotata {
  background: rgba(79, 70, 229, 0.18);
  border-color: rgba(99, 102, 241, 0.5);
  color: #c7d2fe;
}

.badge-rifiutata {
  background: rgba(220, 38, 38, 0.2);
  border-color: rgba(220, 38, 38, 0.45);
  color: #fecaca;
}

.meta {
  font-size: .78rem;
  display: grid;
  gap: 4px;
  text-align: left;
  color:#cbd5e1;
}

.done-label {
  color: #4ade80;
  font-weight: 700;
}

.admin-note {
  font-size: 0.8rem;
  color: #fde68a;
  margin-top: 2px;
}

.step-open-btn {
  margin-left: 0;
  width: auto;
  height: auto;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(30, 41, 59, 0.7);
  color: #dbeafe;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  font-size: 0.78rem;
  line-height: 1;
}
.step-open-btn:hover {
  background: rgba(59, 130, 246, 0.25);
  border-color: rgba(59, 130, 246, 0.5);
  transform: translateY(-1px);
}
.step-open-btn:disabled {
  opacity: 0.45;
  cursor: default;
  transform: none;
}
.step-open-btn .material-icons {
  font-size: 16px;
  line-height: 1;
  color: #ffffff;
}
.step-open-label {
  font-weight: 600;
}

@media (max-width: 719px) {
  .template-container {
    align-content: start;
    gap: 16px;
    padding: 10px 8px 24px;
  }
  .card-head {
    flex-direction: column;
    align-items: flex-start;
  }
  .head-right {
    width: 100%;
    justify-content: flex-start;
  }
  .chev {
    margin-left: auto;
  }
  .step-actions {
    width: 100%;
  }
  .step-open-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (min-width: 720px){
  .card-head{ padding:16px 18px; }
  .card-body { padding: 20px 20px 24px; }
  .steps { gap:18px; }
  .step-row{ padding:16px 18px; }
  .step-header { flex-direction:row; justify-content:space-between; align-items:center; }
  .step-actions { flex-direction: row; align-items: center; justify-content: flex-end; }
  .step-open-btn { width: auto; }
}

/* Fusco Fit user-area standard */
.programs-page {
  --ff-bg: #f5f4f0;
  --ff-surface: rgba(255, 255, 255, 0.74);
  --ff-white: #ffffff;
  --ff-black: #111110;
  --ff-muted: #5f5e5a;
  --ff-subtle: #8a8882;
  --ff-line: rgba(17, 17, 16, 0.1);
  --ff-red: #c8102e;
  --ff-red-dark: #9e0b23;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
  color: var(--ff-black);
  background:
    radial-gradient(circle at 92% 8%, rgba(200, 16, 46, 0.1), transparent 24rem),
    radial-gradient(circle at 4% 72%, rgba(200, 16, 46, 0.05), transparent 26rem),
    var(--ff-bg);
  font-family: 'DM Sans', Avenir, Helvetica, Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  text-align: left;
}

.programs-page,
.programs-page *,
.programs-page *::before,
.programs-page *::after {
  box-sizing: border-box;
}

.programs-page::before {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  content: '';
  opacity: 0.24;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.08'/%3E%3C/svg%3E");
}

.programs-shell {
  position: relative;
  z-index: 1;
  display: grid;
  align-content: start;
  gap: 18px;
  width: min(100%, 980px);
  min-height: calc(100vh - 56px);
  margin: 0 auto;
  padding: clamp(34px, 6vw, 72px) clamp(20px, 5vw, 52px) 148px;
}

.programs-shell > .user-section-header {
  margin-bottom: 0;
}

.programs-page .muted {
  color: var(--ff-muted);
}

.programs-page .loader-min,
.programs-page .programs-empty {
  display: grid;
  min-height: 150px;
  place-items: center;
  padding: 28px;
  color: var(--ff-muted);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 22px;
  background: var(--ff-surface);
  box-shadow: 0 18px 45px rgba(47, 38, 32, 0.08);
  text-align: center;
  backdrop-filter: blur(18px) saturate(135%);
  -webkit-backdrop-filter: blur(18px) saturate(135%);
}

.programs-page .err {
  margin: 0;
  padding: 15px 17px;
  color: var(--ff-red-dark);
  border: 1px solid rgba(200, 16, 46, 0.16);
  border-radius: 15px;
  background: rgba(200, 16, 46, 0.07);
  font-weight: 700;
}

.programs-page .manual-docs {
  padding: clamp(20px, 4vw, 28px);
  border: 1px solid rgba(255, 255, 255, 0.92);
  border-radius: 22px;
  background: var(--ff-surface);
  box-shadow:
    0 18px 45px rgba(47, 38, 32, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(18px) saturate(135%);
  -webkit-backdrop-filter: blur(18px) saturate(135%);
}

.programs-page .manual-card__head h2 {
  margin: 0;
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(1.15rem, 3vw, 1.45rem);
  font-weight: 900;
  letter-spacing: -0.04em;
  text-transform: uppercase;
}

.programs-page .manual-card__head p {
  margin: 7px 0 0;
  color: var(--ff-muted);
}

.programs-page .manual-card__list {
  gap: 9px;
  margin-top: 18px;
}

.programs-page .manual-item {
  padding: 13px 14px;
  border: 1px solid var(--ff-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.72);
}

.programs-page .manual-item__meta strong {
  color: var(--ff-black);
  font-size: 0.96rem;
}

.programs-page .manual-item__meta small,
.programs-page .manual-item__date {
  color: var(--ff-subtle);
}

.programs-page .manual-item__btn {
  min-height: 38px;
  padding: 7px 16px;
  color: var(--ff-white);
  border: 0;
  background: var(--ff-red);
  box-shadow: none;
}

.programs-page .manual-item__btn:hover {
  background: var(--ff-red-dark);
  box-shadow: 0 10px 24px rgba(200, 16, 46, 0.2);
}

.programs-page .group-list {
  align-content: start;
  gap: 16px;
}

.programs-page .group-block {
  position: relative;
  display: grid;
  align-content: start;
  gap: 0;
  overflow: hidden;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.92);
  border-radius: 24px;
  background: var(--ff-surface);
  box-shadow:
    0 20px 50px rgba(47, 38, 32, 0.09),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px) saturate(135%);
  -webkit-backdrop-filter: blur(20px) saturate(135%);
  transition: border-color 200ms ease, transform 200ms ease, box-shadow 200ms ease;
}

.programs-page .group-block:not(.group-block--open) .group-summary {
  min-height: 86px;
  padding-top: 14px;
  padding-bottom: 14px;
}

.programs-page .group-block:not(.group-block--open) .group-title {
  align-self: end;
}

.programs-page .group-block:not(.group-block--open) .group-dates {
  align-self: start;
}

.programs-page .group-block::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1;
  height: 3px;
  content: '';
  background: linear-gradient(90deg, transparent, rgba(200, 16, 46, 0.18), transparent);
}

.programs-page .group-block--active {
  border-color: rgba(200, 16, 46, 0.23);
  box-shadow:
    0 24px 58px rgba(47, 38, 32, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
}

.programs-page .group-block--active::before {
  background: linear-gradient(90deg, var(--ff-red), rgba(200, 16, 46, 0.16));
}

.programs-page .group-block--locked {
  border-color: rgba(17, 17, 16, 0.12);
}

.programs-page .group-summary {
  position: relative;
  grid-template-columns: minmax(0, 1fr) 42px;
  gap: 10px 16px;
  width: 100%;
  min-height: 108px;
  margin: 0;
  padding: clamp(19px, 4vw, 28px);
  color: var(--ff-black);
  border: 0;
  background: transparent;
  font: inherit;
}

.programs-page .group-summary:hover {
  background: rgba(255, 255, 255, 0.36);
}

.programs-page .group-title {
  grid-column: 1;
  gap: 8px;
}

.programs-page .group-title h2 {
  margin: 0;
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(1.2rem, 3.5vw, 1.65rem);
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: -0.045em;
}

.programs-page .group-chip {
  padding: 4px 9px;
  color: var(--ff-muted);
  border: 1px solid rgba(17, 17, 16, 0.1);
  background: rgba(17, 17, 16, 0.045);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.programs-page .group-chip--warn {
  color: #9a6820;
  border-color: rgba(154, 104, 32, 0.18);
  background: rgba(214, 164, 76, 0.12);
}

.programs-page .group-dates {
  grid-column: 1;
  gap: 7px 16px;
  color: var(--ff-subtle);
  font-size: 0.76rem;
}

.programs-page .group-dates strong {
  color: var(--ff-black);
}

.programs-page .group-summary > .chev {
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: center;
  width: 40px;
  height: 40px;
  margin: 0;
  color: var(--ff-white);
  border-radius: 50%;
  background: var(--ff-black);
  font-size: 1rem;
}

.programs-page .group-summary > .chev.open {
  background: var(--ff-red);
}

.programs-page .group-body {
  display: grid;
  gap: 12px;
  padding: 0 clamp(13px, 3vw, 20px) clamp(13px, 3vw, 20px);
  border-top: 1px solid var(--ff-line);
}

.programs-page .card {
  overflow: hidden;
  margin-top: 14px;
  padding: 0;
  border: 1px solid var(--ff-line);
  border-radius: 19px;
  background: rgba(255, 255, 255, 0.68);
  box-shadow: none;
}

.programs-page .card--active {
  border-color: rgba(200, 16, 46, 0.24);
  background: rgba(255, 255, 255, 0.8);
}

.programs-page .card--locked {
  border-color: rgba(17, 17, 16, 0.1);
  background: rgba(230, 228, 223, 0.62);
}

.programs-page .card-head,
.programs-page .card--locked .card-head {
  width: 100%;
  min-height: 78px;
  margin: 0;
  padding: 15px 17px;
  color: var(--ff-black);
  border: 0;
  background: transparent;
  font: inherit;
}

.programs-page .card-head:hover,
.programs-page .card--locked .card-head:hover {
  background: rgba(255, 255, 255, 0.52);
}

.programs-page .head-left .title,
.programs-page .card--locked .head-left .title {
  color: var(--ff-black);
  font-size: 1rem;
  font-weight: 800;
}

.programs-page .head-left .sub {
  color: var(--ff-subtle);
  font-size: 0.75rem;
}

.programs-page .head-right {
  justify-content: flex-end;
}

.programs-page .head-right > .chev {
  width: 30px;
  height: 30px;
  margin: 0 0 0 2px;
  color: var(--ff-black);
  border: 1px solid var(--ff-line);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.74);
}

.programs-page .pill {
  padding: 4px 8px;
  color: var(--ff-muted);
  border-color: rgba(17, 17, 16, 0.09);
  background: rgba(17, 17, 16, 0.055);
  font-size: 0.6875rem;
  font-weight: 700;
}

.programs-page .pill-gray,
.programs-page .badge-todo {
  color: var(--ff-muted);
  border-color: rgba(17, 17, 16, 0.1);
  background: rgba(17, 17, 16, 0.055);
}

.programs-page .pill-amber {
  color: #9a6820;
  border-color: rgba(154, 104, 32, 0.2);
  background: rgba(214, 164, 76, 0.13);
}

.programs-page .pill-green,
.programs-page .pill-active,
.programs-page .badge-done {
  color: #247344;
  border-color: rgba(36, 115, 68, 0.2);
  background: rgba(36, 115, 68, 0.1);
}

.programs-page .pill-locked {
  color: #68645e;
  border-color: rgba(17, 17, 16, 0.1);
  background: #e8e5df;
}

.programs-page .card-body,
.programs-page .card--locked .card-body {
  padding: 15px;
  border-top: 1px solid var(--ff-line);
  background: rgba(245, 244, 240, 0.72);
}

.programs-page .lock-banner {
  align-items: center;
  margin-bottom: 12px;
  padding: 11px 13px;
  color: var(--ff-muted);
  border: 1px dashed rgba(17, 17, 16, 0.18);
  border-radius: 13px;
  background: rgba(17, 17, 16, 0.04);
}

.programs-page .lock-banner__icon {
  color: var(--ff-muted);
}

.programs-page .steps {
  counter-reset: percorso-step;
  gap: 10px;
}

.programs-page .step-row {
  position: relative;
  counter-increment: percorso-step;
  min-height: 92px;
  padding: 16px 16px 16px 66px;
  color: var(--ff-black);
  border: 1px solid var(--ff-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.78);
}

.programs-page .step-row::before {
  position: absolute;
  top: 16px;
  left: 15px;
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  content: counter(percorso-step, decimal-leading-zero);
  color: var(--ff-red);
  border: 1px solid rgba(200, 16, 46, 0.14);
  border-radius: 13px;
  background: rgba(200, 16, 46, 0.075);
  font-family: 'Montserrat', sans-serif;
  font-size: 0.6875rem;
  font-weight: 900;
}

.programs-page .step-row:hover {
  border-color: rgba(200, 16, 46, 0.22);
  background: rgba(255, 255, 255, 0.96);
}

.programs-page .step-row--complete {
  border-color: rgba(36, 115, 68, 0.18);
  background: rgba(244, 250, 246, 0.82);
}

.programs-page .step-row--complete::before {
  color: #247344;
  border-color: rgba(36, 115, 68, 0.16);
  background: rgba(36, 115, 68, 0.09);
}

.programs-page .step-row--locked {
  opacity: 0.62;
  filter: grayscale(0.25);
}

.programs-page .step-title {
  color: var(--ff-black);
  font-weight: 800;
}

.programs-page .badge {
  padding: 4px 8px;
  color: var(--ff-muted);
  border-color: rgba(17, 17, 16, 0.09);
  background: rgba(17, 17, 16, 0.055);
  font-size: 0.6875rem;
  font-weight: 700;
}

.programs-page .badge-next,
.programs-page .badge-type,
.programs-page .badge-prenotata {
  color: var(--ff-red-dark);
  border-color: rgba(200, 16, 46, 0.17);
  background: rgba(200, 16, 46, 0.08);
}

.programs-page .badge-update,
.programs-page .badge-warn,
.programs-page .update-note {
  color: #a66719;
}

.programs-page .badge-update,
.programs-page .badge-warn {
  border-color: rgba(166, 103, 25, 0.2);
  background: rgba(214, 164, 76, 0.12);
}

.programs-page .badge-rifiutata {
  color: var(--ff-red-dark);
  border-color: rgba(200, 16, 46, 0.18);
  background: rgba(200, 16, 46, 0.08);
}

.programs-page .meta {
  color: var(--ff-subtle);
  font-size: 0.74rem;
}

.programs-page .done-label {
  color: #247344;
}

.programs-page .step-open-btn {
  min-height: 38px;
  padding: 8px 12px;
  color: var(--ff-white);
  border: 0;
  border-radius: 999px;
  background: var(--ff-red);
  font: inherit;
  font-size: 0.72rem;
  font-weight: 700;
}

.programs-page .step-open-btn:hover {
  border: 0;
  background: var(--ff-red-dark);
}

.programs-page .step-open-btn .material-icons {
  color: currentColor;
}

.programs-page :is(button, a):focus-visible {
  outline: 3px solid rgba(200, 16, 46, 0.28);
  outline-offset: 3px;
}

@media (max-width: 719px) {
  .programs-shell {
    min-height: calc(100vh - 50px);
    gap: 12px;
    padding: 28px 12px 138px;
  }

  .programs-page .manual-docs,
  .programs-page .group-block {
    border-radius: 19px;
  }

  .programs-page .manual-item {
    align-items: flex-start;
    flex-direction: column;
  }

  .programs-page .manual-item__actions,
  .programs-page .manual-item__btn {
    width: 100%;
  }

  .programs-page .group-summary {
    grid-template-columns: minmax(0, 1fr) 38px;
    min-height: 96px;
    padding: 18px 15px;
  }

  .programs-page .group-block:not(.group-block--open) .group-summary {
    min-height: 82px;
    gap: 4px 10px;
    padding: 12px 14px;
  }

  .programs-page .group-summary > .chev {
    width: 36px;
    height: 36px;
  }

  .programs-page .group-body {
    padding: 0 10px 10px;
  }

  .programs-page .card-head,
  .programs-page .card--locked .card-head {
    align-items: flex-start;
    flex-direction: column;
    padding: 14px;
  }

  .programs-page .head-right {
    width: 100%;
    justify-content: flex-start;
  }

  .programs-page .head-right > .chev {
    margin-left: auto;
  }

  .programs-page .card-body,
  .programs-page .card--locked .card-body {
    padding: 11px;
  }

  .programs-page .step-row {
    padding: 14px 13px 14px 58px;
  }

  .programs-page .step-row::before {
    top: 14px;
    left: 12px;
    width: 34px;
    height: 34px;
    border-radius: 11px;
  }

  .programs-page .step-actions,
  .programs-page .step-open-btn {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .programs-page .group-block,
  .programs-page .chev,
  .programs-page .manual-item__btn,
  .programs-page .step-row,
  .programs-page .step-open-btn {
    transition: none;
  }
}
</style>
